import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ActivitySidebar from './components/ActivitySidebar';
import TimelineCanvas from './components/TimelineCanvas';
import CostTracker from './components/CostTracker';
import SaveShareModal from './components/SaveShareModal';
import RecommendationPanel from './components/RecommendationPanel';

const ItineraryBuilder = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSaveShareModalOpen, setIsSaveShareModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [tripInfo, setTripInfo] = useState({
    destination: 'New Trip',
    startDate: new Date()?.toISOString()?.split('T')?.[0],
    endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    travelers: 2
  });

  const [itinerary, setItinerary] = useState([
    {
      day: 1,
      date: new Date()?.toISOString()?.split('T')?.[0],
      title: 'Day 1',
      timeSlots: [
        { id: 'slot-1-1', time: '08:00 AM', activity: null },
        { id: 'slot-1-2', time: '10:00 AM', activity: null },
        { id: 'slot-1-3', time: '12:00 PM', activity: null },
        { id: 'slot-1-4', time: '02:00 PM', activity: null },
        { id: 'slot-1-5', time: '04:00 PM', activity: null },
        { id: 'slot-1-6', time: '06:00 PM', activity: null },
        { id: 'slot-1-7', time: '08:00 PM', activity: null }
      ],
      accommodation: null
    }
  ]);

  const availableActivities = [
    { id: 'act-1', title: 'Island Hopping Tour', category: 'activity', duration: 4, cost: 1500, description: 'Visit multiple islands', icon: 'Waves', status: 'available' },
    { id: 'act-2', title: 'Beach Resort Stay', category: 'accommodation', duration: 24, cost: 3500, description: 'Luxury beachfront resort', icon: 'Hotel', status: 'available' },
    { id: 'act-3', title: 'Seafood Lunch', category: 'food', duration: 1, cost: 800, description: 'Fresh local seafood', icon: 'Utensils', status: 'available' },
    { id: 'act-4', title: 'Airport Transfer', category: 'transport', duration: 1, cost: 500, description: 'Private van service', icon: 'Car', status: 'available' },
    { id: 'act-5', title: 'Spa & Massage', category: 'wellness', duration: 2, cost: 1200, description: 'Relaxing spa treatment', icon: 'Heart', status: 'available' },
    { id: 'act-6', title: 'City Tour', category: 'sightseeing', duration: 3, cost: 900, description: 'Historical landmarks tour', icon: 'Camera', status: 'available' },
    { id: 'act-7', title: 'Local Market Visit', category: 'shopping', duration: 2, cost: 500, description: 'Traditional crafts and souvenirs', icon: 'ShoppingBag', status: 'available' },
    { id: 'act-8', title: 'Cultural Show', category: 'entertainment', duration: 2, cost: 600, description: 'Traditional dance performance', icon: 'Music', status: 'available' },
    { id: 'act-9', title: 'Snorkeling Adventure', category: 'activity', duration: 3, cost: 1000, description: 'Explore coral reefs', icon: 'Waves', status: 'available' },
    { id: 'act-10', title: 'Sunset Dinner Cruise', category: 'food', duration: 3, cost: 2500, description: 'Romantic dinner on the water', icon: 'Utensils', status: 'confirmed' },
    { id: 'act-11', title: 'Mountain Hiking', category: 'activity', duration: 5, cost: 800, description: 'Guided mountain trek', icon: 'Waves', status: 'pending' },
    { id: 'act-12', title: 'Cooking Class', category: 'food', duration: 3, cost: 1500, description: 'Learn local cuisine', icon: 'Utensils', status: 'available' }
  ];

  const [favorites, setFavorites] = useState(['act-10', 'act-2']);
  const [showRecommendations, setShowRecommendations] = useState(false);

  const handleDragStart = (e, activity) => {
    e?.dataTransfer?.setData('activity', JSON.stringify(activity));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDrop = (dayIndex, slotIndex) => {
    return (e) => {
      e?.preventDefault();
      const activityData = JSON.parse(e?.dataTransfer?.getData('activity'));
      
      const newItinerary = [...itinerary];
      const slot = newItinerary?.[dayIndex]?.timeSlots?.[slotIndex];
      
      if (!slot?.activity) {
        slot.activity = {
          ...activityData,
          bookedId: `booked-${Date.now()}`
        };
        setItinerary(newItinerary);
      }
    };
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleRemoveActivity = (dayIndex, slotIndex) => {
    const newItinerary = [...itinerary];
    newItinerary[dayIndex].timeSlots[slotIndex].activity = null;
    setItinerary(newItinerary);
  };

  const handleAddActivityFromRecommendation = (recommendation) => {
    const activity = availableActivities?.find(act => act?.id === recommendation?.activityId);
    if (!activity) return;

    const targetDay = recommendation?.suggestedDay - 1;
    if (targetDay >= 0 && targetDay < itinerary?.length) {
      const newItinerary = [...itinerary];
      const emptySlot = newItinerary?.[targetDay]?.timeSlots?.find(slot => !slot?.activity);
      
      if (emptySlot) {
        emptySlot.activity = {
          ...activity,
          bookedId: `booked-${Date.now()}`
        };
        setItinerary(newItinerary);
      }
    }
  };

  const handleOptimizeItinerary = (optimizedItinerary) => {
    if (!optimizedItinerary) return;

    const newItinerary = itinerary?.map((day, dayIndex) => {
      const optimizedDay = optimizedItinerary?.find(od => od?.day === day?.day);
      if (!optimizedDay) return day;

      const updatedTimeSlots = [...day?.timeSlots];
      optimizedDay?.activities?.forEach((optActivity, index) => {
        const activity = availableActivities?.find(act => act?.id === optActivity?.activityId);
        if (activity && updatedTimeSlots?.[index]) {
          updatedTimeSlots[index].activity = {
            ...activity,
            bookedId: `booked-${Date.now()}-${index}`
          };
        }
      });

      return { ...day, timeSlots: updatedTimeSlots };
    });

    setItinerary(newItinerary);
  };

  const handleAddDay = () => {
    const lastDay = itinerary?.[itinerary?.length - 1];
    const nextDate = new Date(lastDay.date);
    nextDate?.setDate(nextDate?.getDate() + 1);
    
    const newDay = {
      day: itinerary?.length + 1,
      date: nextDate?.toISOString()?.split('T')?.[0],
      title: `Day ${itinerary?.length + 1}`,
      timeSlots: [
        { id: `slot-${itinerary?.length + 1}-1`, time: '08:00 AM', activity: null },
        { id: `slot-${itinerary?.length + 1}-2`, time: '10:00 AM', activity: null },
        { id: `slot-${itinerary?.length + 1}-3`, time: '12:00 PM', activity: null },
        { id: `slot-${itinerary?.length + 1}-4`, time: '02:00 PM', activity: null },
        { id: `slot-${itinerary?.length + 1}-5`, time: '04:00 PM', activity: null },
        { id: `slot-${itinerary?.length + 1}-6`, time: '06:00 PM', activity: null },
        { id: `slot-${itinerary?.length + 1}-7`, time: '08:00 PM', activity: null }
      ],
      accommodation: null
    };
    
    setItinerary([...itinerary, newDay]);
  };

  const handleToggleFavorite = (activityId) => {
    setFavorites(prev => 
      prev?.includes(activityId) 
        ? prev?.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const calculateTotalCost = () => {
    let total = 0;
    itinerary?.forEach(day => {
      day?.timeSlots?.forEach(slot => {
        if (slot?.activity) {
          total += slot?.activity?.cost || 0;
        }
      });
      if (day?.accommodation) {
        total += day?.accommodation?.cost || 0;
      }
    });
    return total;
  };

  const getCostBreakdown = () => {
    const breakdown = {
      activities: 0,
      accommodation: 0,
      food: 0,
      transport: 0
    };

    itinerary?.forEach(day => {
      day?.timeSlots?.forEach(slot => {
        if (slot?.activity) {
          const cost = slot?.activity?.cost || 0;
          if (slot?.activity?.category === 'food') {
            breakdown.food += cost;
          } else if (slot?.activity?.category === 'transport') {
            breakdown.transport += cost;
          } else if (slot?.activity?.category === 'accommodation') {
            breakdown.accommodation += cost;
          } else {
            breakdown.activities += cost;
          }
        }
      });
      if (day?.accommodation) {
        breakdown.accommodation += day?.accommodation?.cost || 0;
      }
    });

    return breakdown;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20">
        {/* Top Bar */}
        <div className="bg-card border-b border-border px-4 md:px-6 py-4">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/user-dashboard')}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={16} />
                Back
              </button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground">
                  {tripInfo?.destination}
                </h1>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    {new Date(tripInfo.startDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    {' - '}
                    {new Date(tripInfo.endDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Users" size={12} />
                    {tripInfo?.travelers} travelers
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRecommendations(!showRecommendations)}
                iconName="Sparkles"
                iconPosition="left"
              >
                {showRecommendations ? 'Hide' : 'Show'} AI Suggestions
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={() => setIsSaveShareModalOpen(true)}
              >
                Export
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Save"
                onClick={() => setIsSaveShareModalOpen(true)}
              >
                Save & Share
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-6 pb-6">
          {/* Sidebar */}
          <ActivitySidebar
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            activities={availableActivities}
            favorites={favorites}
            selectedCategory={selectedCategory}
            searchTerm={searchTerm}
            onCategoryChange={setSelectedCategory}
            onSearchChange={setSearchTerm}
            onToggleFavorite={(id) => {
              setFavorites(prev => 
                prev?.includes(id) ? prev?.filter(fav => fav !== id) : [...prev, id]
              );
            }}
            onDragStart={handleDragStart}
          />

          {/* Timeline Canvas */}
          <div className="flex-1 flex flex-col gap-6 py-6 pr-6">
            {showRecommendations && (
              <RecommendationPanel
                tripInfo={tripInfo}
                itinerary={itinerary}
                availableActivities={availableActivities}
                onAddActivity={handleAddActivityFromRecommendation}
                onOptimizeItinerary={handleOptimizeItinerary}
              />
            )}

            <TimelineCanvas
              itinerary={itinerary}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onRemoveActivity={handleRemoveActivity}
              onAddDay={handleAddDay}
            />
          </div>

          {/* Cost Tracker */}
          <CostTracker
            totalCost={calculateTotalCost()}
            breakdown={getCostBreakdown()}
            travelers={tripInfo?.travelers}
          />
        </div>
      </div>
      {/* Save & Share Modal */}
      <SaveShareModal
        isOpen={isSaveShareModalOpen}
        onClose={() => setIsSaveShareModalOpen(false)}
        tripInfo={tripInfo}
        itinerary={itinerary}
      />
    </div>
  );
};

export default ItineraryBuilder;